import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Rocket, TrendingUp, Code2, Zap, Shield, ArrowLeft, Target, Gauge, Cpu, Palette, Network } from 'lucide-react';

// Container principal
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
`;

// Navbar (similar à landing)
const Navbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const NavContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  cursor: pointer;

  img {
    height: 40px;
    width: auto;
    transition: transform ${({ theme }) => theme.animations.duration.fast};
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: color ${({ theme }) => theme.animations.duration.fast};

  ${LogoWrapper}:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Conteúdo principal
const MainContent = styled.main`
  flex: 1;
  padding: 120px ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[12]};
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 100px ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[8]};
  }
`;

const Title = styled.h1`
  font-size: clamp(48px, 8vw, 80px);
  font-weight: 700;
  line-height: 1.1;
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 auto ${({ theme }) => theme.spacing[12]};
  max-width: 700px;
`;

const Section = styled(motion.section)`
  margin-bottom: ${({ theme }) => theme.spacing[12]};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SectionText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[8]};
  margin-top: ${({ theme }) => theme.spacing[8]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[8]};
  }
`;

const ValueCard = styled(motion.div)`
  padding: ${({ theme }) => theme.spacing[8]};
  background: transparent;
  border-left: 3px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.animations.duration.normal};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -3px;
    top: 0;
    width: 3px;
    height: 0;
    background: ${({ theme }) => theme.colors.primary};
    transition: height ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeOut};
  }

  &:hover {
    background: rgba(255, 59, 48, 0.02);

    &::before {
      height: 100%;
    }
  }
`;

const ValueTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ValueText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin-left: ${({ theme }) => theme.spacing[10]};
`;

const IconWrapper = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

// Footer (similar à landing)
const Footer = styled.footer`
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
`;

const FooterContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const Philosophy: React.FC = () => {
  const navigate = useNavigate();

  // Scroll to top quando página carrega
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <PageWrapper>
      <Navbar>
        <NavContent>
          <LogoWrapper onClick={() => navigate('/')}>
            <img src="/icons/loopra_icon_profile.webp" alt="Loopra" />
            <LogoText>Loopra</LogoText>
          </LogoWrapper>
          <BackButton onClick={() => navigate('/')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
        </NavContent>
      </Navbar>

      <MainContent>
        <Title>Nossa Filosofia</Title>
        <Subtitle>Tecnologia que amplifica o potencial humano</Subtitle>

        <Section {...fadeInUp}>
          <SectionTitle>
            <IconWrapper><Rocket size={36} /></IconWrapper>
            Nossa Missão
          </SectionTitle>
          <SectionText>
            Democratizar o acesso à automação inteligente, permitindo que empresas de todos os
            tamanhos conectem suas plataformas de comunicação (WhatsApp, Gmail, Slack) de forma
            simples, poderosa e escalável. Acreditamos que a inteligência artificial deve servir
            ao ser humano, amplificando sua criatividade e produtividade, nunca substituindo o
            toque humano que faz cada negócio único.
          </SectionText>
        </Section>

        <Section {...fadeInUp}>
          <SectionTitle>
            <IconWrapper><TrendingUp size={36} /></IconWrapper>
            Nossa Visão
          </SectionTitle>
          <SectionText>
            Ser a plataforma líder em automação multi-canal na América Latina até 2027, reconhecida
            não apenas por nossa tecnologia de ponta, mas por nossa interface intuitiva, design
            futurista e foco absoluto na experiência do usuário. Queremos que cada interação com
            Loopra seja fluida, elegante e, acima de tudo, humana.
          </SectionText>
        </Section>

        <Section {...fadeInUp}>
          <SectionTitle>
            <IconWrapper><Target size={36} /></IconWrapper>
            Nossos Valores
          </SectionTitle>
          <ValuesGrid>
            <ValueCard>
              <ValueTitle>
                <IconWrapper><Network size={24} /></IconWrapper>
                Integração Multi-Canal
              </ValueTitle>
              <ValueText>
                Conecte WhatsApp, Gmail e Slack em uma única plataforma. Configure fluxos personalizados
                sem código. Tudo sincronizado, tudo automatizado.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueTitle>
                <IconWrapper><Palette size={24} /></IconWrapper>
                Design System Profissional
              </ValueTitle>
              <ValueText>
                Interface única, futurista e funcional. Cada pixel pensado. Design tokens centralizados,
                componentes reutilizáveis.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueTitle>
                <IconWrapper><Shield size={24} /></IconWrapper>
                Segurança & Privacidade
              </ValueTitle>
              <ValueText>
                Seus dados são seus. Multi-tenant isolado, autenticação robusta, zero tracking desnecessário.
                Transparência total.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueTitle>
                <IconWrapper><Zap size={24} /></IconWrapper>
                Entrega Rápida
              </ValueTitle>
              <ValueText>
                Iteração semanal, deploys frequentes, feedback direto. Construímos rápido, testamos rápido,
                melhoramos rápido.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueTitle>
                <IconWrapper><Gauge size={24} /></IconWrapper>
                Alta Performance
              </ValueTitle>
              <ValueText>
                Operações assíncronas, cache inteligente, lazy loading. Otimização em cada camada.
                Rápido de verdade.
              </ValueText>
            </ValueCard>

            <ValueCard>
              <ValueTitle>
                <IconWrapper><Cpu size={24} /></IconWrapper>
                Arquitetura Escalável
              </ValueTitle>
              <ValueText>
                API-first, desacoplamento total, microservices ready. Cresce com você, do MVP ao enterprise.
                Sem refactor traumático.
              </ValueText>
            </ValueCard>
          </ValuesGrid>
        </Section>
      </MainContent>

      <Footer>
        <FooterContent>
          © 2025 Loopra. Todos os direitos reservados.
        </FooterContent>
      </Footer>
    </PageWrapper>
  );
};

export default Philosophy;
